import type { IndicatorDef, InputSpec, OutputSpec } from './types'

/**
 * Version 1 of every id that predates the library: the klinecharts BUILT-IN
 * template, untouched, under its original engine name. Users who already have
 * one keep it (visible, editable), nobody can add one. The input schema is the
 * template's positional calcParams as `p1..pn`; a saved instance may carry
 * fewer (klinecharts regenerates one figure per param for MA/EMA/RSI/WR/BIAS).
 */

export interface BuiltinSpec {
  id: string
  label: string
  pane: 'main' | 'sub'
  /** klinecharts' default calcParams (defs/<id>/v1.meta.ts quotes the engine source). */
  params: number[]
  labels?: string[]
  kinds?: Array<'int' | 'float'>
  /** Figure keys at the default params (informational; legends read the live figures). */
  outputs: string[]
  histogram?: string
  dots?: string
  levels?: number[]
  /** False for path-dependent / cumulative studies (SAR, OBV, PVT, AVP). */
  tailSafe?: boolean
}

/** Figure keys that klinecharts draws as bars / circles; everything else is a line. */
function plotOf(spec: BuiltinSpec, key: string): OutputSpec['plot'] {
  if (spec.histogram === key) return 'histogram'
  if (spec.dots === key) return 'dots'
  return 'line'
}

export function builtinV1(spec: BuiltinSpec): IndicatorDef {
  const inputs: InputSpec[] = spec.params.map((d, i) =>
    spec.kinds?.[i] === 'float'
      ? { key: `p${i + 1}`, kind: 'float', label: spec.labels?.[i] ?? `Param ${i + 1}`, default: d, min: 0 }
      : { key: `p${i + 1}`, kind: 'int', label: spec.labels?.[i] ?? `Param ${i + 1}`, default: d, min: 1 },
  )
  const outputs: OutputSpec[] = spec.outputs.map((key) => ({ key, label: key.toUpperCase(), plot: plotOf(spec, key), color: '' }))
  return {
    id: spec.id,
    version: 1,
    label: spec.label,
    pane: spec.pane,
    series: spec.pane === 'main' ? 'price' : 'normal',
    precision: 2,
    inputs,
    outputs,
    ...(spec.levels ? { levels: spec.levels.map((value) => ({ value })) } : {}),
    listed: false,
    tailSafe: spec.tailSafe ?? true,
    lookback: () => 0,
    builtin: true,
  }
}
